/**
 * „Filter · n“ öffnet „Verlauf eingrenzen“ (Boards „Verlauf · Filter“ und „Handy · Filter“): am
 * Desktop ein Panel am Knopf, am Handy ein Blatt. Geändert wird ein Entwurf; „Anzeigen“ übernimmt
 * ihn, „Zurücksetzen“ leert ihn, Schließen verwirft ihn.
 */
import * as Popover from '@radix-ui/react-popover';
import { SlidersHorizontal, X } from 'lucide-react';
import { useState } from 'react';
import type { DynamicPricingHistoryView } from '@/shared/api';
import { BottomSheet, Button } from '@/shared/components';
import { HistoryFilterFields } from './HistoryFilterFields';
import { emptyDraft, type HistoryFilterDraft } from './historyFilterDraft';

interface Props {
  view: DynamicPricingHistoryView;
  count: number;
  mobile: boolean;
  /** Entwurf aus dem aktuellen Stand der URL. */
  current: () => HistoryFilterDraft;
  onApply: (draft: HistoryFilterDraft) => void;
}

export function HistoryFilterPanel({ view, count, mobile, current, onApply }: Props) {
  const [draft, setDraft] = useState<HistoryFilterDraft | null>(null);
  const open = draft !== null;
  const toggle = (next: boolean) => setDraft(next ? current() : null);
  const trigger = (
    <button
      type="button"
      aria-expanded={open}
      // Am Desktop schaltet der Popover-Auslöser selbst.
      onClick={mobile ? () => toggle(!open) : undefined}
      className="border-ink rounded-pill text-ink flex h-11 items-center gap-2 border bg-white px-3.5 text-[13px] font-bold md:h-10"
    >
      <SlidersHorizontal size={14} aria-hidden />
      {count ? `Filter · ${count}${mobile ? '' : ' aktiv'}` : 'Filter'}
    </button>
  );
  const footer = (
    <div className="flex justify-end gap-2">
      <Button variant="secondary" className="min-h-11" onClick={() => setDraft(emptyDraft())}>
        Zurücksetzen
      </Button>
      <Button
        variant="primary"
        className="min-h-11"
        onClick={() => {
          if (draft) onApply(draft);
          setDraft(null);
        }}
      >
        Anzeigen
      </Button>
    </div>
  );
  const fields = draft && <HistoryFilterFields view={view} draft={draft} onChange={setDraft} />;
  if (mobile) {
    return (
      <>
        {trigger}
        <BottomSheet open={open} onOpenChange={toggle} title="Verlauf eingrenzen" footer={footer}>
          <div className="py-1">{fields}</div>
        </BottomSheet>
      </>
    );
  }
  return (
    <Popover.Root open={open} onOpenChange={toggle}>
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          role="dialog"
          aria-label="Verlauf eingrenzen"
          align="start"
          sideOffset={8}
          collisionPadding={16}
          className="border-hairline bg-card rounded-tile shadow-panel z-40 flex max-h-[min(var(--radix-popover-content-available-height),calc(100dvh-2rem))] w-[520px] max-w-[calc(100vw-2rem)] flex-col gap-3 overflow-hidden border p-[18px]"
        >
          <span className="flex items-center gap-2">
            <h2 className="grow font-serif text-xl font-bold">Verlauf eingrenzen</h2>
            <Popover.Close
              aria-label="Filter schließen"
              className="border-hairline rounded-pill flex size-9 items-center justify-center border"
            >
              <X size={16} aria-hidden />
            </Popover.Close>
          </span>
          {/* Nur die Felder scrollen; „Anzeigen“ bleibt sichtbar. */}
          <div className="-mx-1 min-h-0 overflow-y-auto px-1">{fields}</div>
          {footer}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
